import { defineStore } from "pinia";

import { SearchState, Hit, Filters } from "../interfaces/search";
import { Filter, Facet, FacetCount, Result } from "../interfaces/facets";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";

const whitepaperIndex = import.meta.env.VITE_WHITEPAPERS_INDEX ?? "wdp_production_whitepapers";
const fileManagerIndex = import.meta.env.VITE_FILE_MANAGER_INDEX ?? "wdp_production_file_manager_docs";

	const fullFacetIndexes = new Set([
		import.meta.env.VITE_BLOG_INDEX ?? "wdp_production_blog",
		import.meta.env.VITE_DOSSIERS_INDEX ?? "wdp_production_dossiers",
		import.meta.env.VITE_EVENTS_INDEX ?? "wdp_production_events",
		import.meta.env.VITE_PRESS_INDEX ?? "wdp_production_press",
	import.meta.env.VITE_STUDIES_INDEX ?? "wdp_production_studies",
	import.meta.env.VITE_CORPORATE_HUB_INDEX ?? "wdp_production_corporate_hub",
	import.meta.env.VITE_MARKETING ?? "wdp_production_marketing",
	import.meta.env.VITE_PILLARS ?? "wdp_production_pillars",
	import.meta.env.VITE_SOLUTIONS ?? "wdp_production_solutions",
	fileManagerIndex,
]);

function facetByForCollection(collection: string): string | null {
	if (collection === fileManagerIndex) {
		return "type";
	}
	if (collection === whitepaperIndex) {
		return "type,pillars,postDateYear";
	}
	if (fullFacetIndexes.has(collection)) {
		return "type,pillars,postDateYear,industries,solutions";
	}
	return null;
}

export const useSearchStore = defineStore("search", {
	state: (): SearchState => ({
		loading: false,
		facets: [],
		filters: {
			filterQuery: `site:=${window.language} && hideInFrontEndSearch:=false`,
			searchQuery: "*",
			type: {
				field: "type",
				values: [],
				combination: "&&",
			},
			industries: {
				field: "industries",
				values: [],
				combination: "&&",
			},
			solutions: {
				field: "solutions",
				values: [],
				combination: "&&",
			},
		},
		pagination: {
			current: 1,
			totalPages: 0,
			totalResults: 0,
			foundResults: 0,
			itemsPerPage: 24,
		},
		sorting: [
			{
				type: "post_date_timestamp",
				order: "desc",
			},
			{
				type: "title",
				order: "asc",
			},
		],
		indexes: [
			import.meta.env.VITE_BLOG_INDEX ?? "wdp_production_blog",
			import.meta.env.VITE_CORPORATE_HUB_INDEX ?? "wdp_production_corporate_hub",
			import.meta.env.VITE_EVENTS_INDEX ?? "wdp_production_events",
			import.meta.env.VITE_GROUP_STRUCTURE_INDEX ?? "wdp_production_group_structure",
			import.meta.env.VITE_HR_INDEX ?? "wdp_production_hr",
			import.meta.env.VITE_INVESTORS ?? "wdp_production_investors",
			import.meta.env.VITE_LOCATIONS_INDEX ?? "wdp_production_locations",
			import.meta.env.VITE_MARKETING ?? "wdp_production_marketing",
			import.meta.env.VITE_PILLARS ?? "wdp_production_pillars",
			import.meta.env.VITE_PRESS_INDEX ?? "wdp_production_press",
			import.meta.env.VITE_SOLUTIONS ?? "wdp_production_solutions",
			import.meta.env.VITE_STUDIES_INDEX ?? "wdp_production_studies",
			import.meta.env.VITE_WHITEPAPERS_INDEX ?? "wdp_production_whitepapers",
			import.meta.env.VITE_FILE_MANAGER_INDEX ?? "wdp_production_file_manager_docs"
		],
		indexesKnowledgeHub: [
			import.meta.env.VITE_BLOG_INDEX ?? "wdp_production_blog",
			import.meta.env.VITE_EVENTS_INDEX ?? "wdp_production_events",
			import.meta.env.VITE_PRESS_INDEX ?? "wdp_production_press",
			import.meta.env.VITE_STUDIES_INDEX ?? "wdp_production_studies",
			import.meta.env.VITE_WHITEPAPERS_INDEX ?? "wdp_production_whitepapers",
			import.meta.env.VITE_FILE_MANAGER_INDEX ?? "wdp_production_file_manager_docs",
		],
		results: null,
		cards: null,
	}),
	actions: {
		async fetch() {
			this.loading = true;
			this.cards = null;
			this.setFiltersQuery();

			if (this.filters.searchQuery === "*") {
				// sort by post date
				this.sorting[0].type = "post_date_timestamp";
			} else {
				// sort by text match
				this.sorting[0].type = "_text_match";
			}

			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting, "title, description");
			const indexes = this.filters.searchQuery === "*" ? this.indexesKnowledgeHub : this.indexes;

			this.pagination.current = 1;

			const searchRequests = {
				searches: indexes.map((index: string) => {
					const facetBy = facetByForCollection(index);
					return {
						highlight_full_fields: "title, description, meta, slug",
						collection: index,
						page: 1,
						per_page: this.filters.searchQuery === "*" ? 20 : 50,
						max_facet_values: 100,
						...(facetBy ? { facet_by: facetBy } : {}),
						...searchParameters,
					};
				}),
			};

			await client.multiSearch.perform(searchRequests).then((results: any) => {
				if (results) {
					this.pagination.current = 1;
					this.facets = [];
					const rawFacets: Array<Facet> = [];

					results.results.forEach((result: Result) => {
						if (result.facet_counts) {
							result.facet_counts.forEach((count) => {
								rawFacets.push(count);
							});
						}
					});

					const type = this.getMergedFacets(
						rawFacets.filter((facet) => facet.field_name === "type"),
						"type",
					);
					const industries = this.getMergedFacets(
						rawFacets.filter((facet) => facet.field_name === "industries"),
						"industries",
					);
					const solutions = this.getMergedFacets(
						rawFacets.filter((facet) => facet.field_name === "solutions"),
						"solutions",
					);

					if (type && this.facets) this.facets.push(type);
					if (industries && this.facets) this.facets.push(industries);
					if (solutions && this.facets) this.facets.push(solutions);

					this.pagination.totalResults = results.results.reduce(
						(count, result) => count + (result?.hits?.length ?? 0),
						0,
					);
					this.pagination.totalPages = Math.ceil(this.pagination.totalResults / this.pagination.itemsPerPage);

					this.results = [...results.results.map((res: { hits: Array<Hit> }) => res?.hits ?? [])].flat();

					if (this.filters.searchQuery === "*") {
						this.results.sort((a: Hit, b: Hit) => {
							if (a?.document && b?.document) {
								return a.document.post_date_timestamp < b.document.post_date_timestamp ? 1 : -1;
							}
							return -1;
						});
					} else {
						this.results.sort((a: Hit, b: Hit) => {
							if (a?.text_match && b?.text_match) {
								return a.text_match < b.text_match ? 1 : -1;
							}
							return -1;
						});
					}

					this.setPagination(1);
				}
			});
		},
		setPagination(page: number) {
			this.loading = true;
			this.pagination.current = page;

			this.cards = [];

			setTimeout(() => {
				if (this.results) {
					const startIndex = this.pagination.itemsPerPage * ((this.pagination.current ?? 1) - 1);
					this.cards = this.results.slice(startIndex, startIndex + (this.pagination.itemsPerPage ?? 7));
					this.loading = false;
				}
			}, 1000);
		},
		setSearch(value: string) {
			this.filters.searchQuery = value === "" ? "*" : value;
			this.fetch();
		},
		setFilter(type: keyof Filters, value: string) {
			const settedFilter = this.filters[type];

			if (typeof settedFilter !== "string" && settedFilter && "values" in settedFilter) {
				if (settedFilter.values.indexOf(value) < 0) {
					settedFilter.values.push(value);
				} else {
					settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
				}
			}

			this.setFiltersQuery();
			this.fetch();
		},
		clearTypeFilter() {
			this.filters.type.values = [];
			this.setFiltersQuery();
			this.fetch();
		},
		setFiltersQuery() {
			const filterParts: string[] = [];
			const language = window.language;

			filterParts.push(`site:=${language}`);
			filterParts.push(`hideInFrontEndSearch:=false`);

			if (this.filters.type.values.length > 0) {
				filterParts.push(`${this.filters.type.field}:=[${this.filters.type.values.join(",")}]`);
			}
			if (this.filters.industries.values.length > 0) {
				filterParts.push(`${this.filters.industries.field}:=[${this.filters.industries.values.join(",")}]`);
			}
			if (this.filters.solutions.values.length > 0) {
				filterParts.push(`${this.filters.solutions.field}:=[${this.filters.solutions.values.join(",")}]`);
			}

			this.filters.filterQuery = filterParts.join(" && ");
		},
		getMergedFacets(facets: Array<Facet>, type: string) {
			const facet: Facet = {
				counts: [],
				field_name: type,
				stats: {
					total_values: 0,
				},
			};

			facets.forEach((f: Facet) => {
				f.counts.forEach((count: FacetCount) => {
					facet.counts.push(count);
				});
			});

			facet.counts = [...new Map(facet.counts.map((item) => [item["highlighted"], item])).values()];

			if (facet.counts.length > 0) {
				return facet;
			}

			return null;
		},
	},
});
